import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  and,
  or
} from 'firebase/firestore'
import { db } from './firebase'
import { Meeting, MeetingListItem, CreateMeetingRequest, MeetingFilters } from '../types/meeting'
import { convertMeetingTimeFromGMT } from '../utils/timezoneUtils'
import { Review } from '../types/review'
import { getUserDocument } from './userService'

/**
 * Efficiently fetches profile images for meetings
 */
const enrichMeetingsWithProfileImages = async (
  meetings: MeetingListItem[],
  currentUserId: string
): Promise<MeetingListItem[]> => {
  try {
    // Get unique user IDs we need to fetch
    const userIds = new Set<string>()
    meetings.forEach(meeting => {
      // Add the other participant's ID
      if (currentUserId === meeting.clientId) {
        userIds.add(meeting.professionalId)
      } else {
        userIds.add(meeting.clientId)
      }
    })

    // Fetch all user profiles in parallel
    const userProfiles = new Map<string, { profileImage?: string }>()
    await Promise.all(
      Array.from(userIds).map(async (userId) => {
        try {
          const profile = await getUserDocument(userId)
          if (profile) {
            userProfiles.set(userId, { profileImage: profile.profileImage })
          }
        } catch (error) {
          // Skip profiles that can't be fetched (expected for permission-restricted profiles)
          if (error instanceof Error && !error.message.includes('Permission denied')) {
            console.warn(`Unable to fetch profile for user ${userId}:`, error.message)
          }
        }
      })
    )

    // Enrich meetings with profile images
    return meetings.map(meeting => {
      const otherUserId = currentUserId === meeting.clientId
        ? meeting.professionalId
        : meeting.clientId
      const profile = userProfiles.get(otherUserId)

      return {
        ...meeting,
        imageUrl: meeting.imageUrl || profile?.profileImage
      }
    })
  } catch (error) {
    console.error('Error enriching meetings with profile images:', error)
    return meetings
  }
}

/**
 * Efficiently fetches review ratings for meetings using batch queries
 * Aligns with DATABASE_SCHEMA.md: Uses clientReviewId/professionalReviewId fields
 */
const enrichMeetingsWithRatings = async (
  meetings: MeetingListItem[],
  currentUserId: string
): Promise<MeetingListItem[]> => {
  try {
    // Extract review IDs from meetings (batch approach)
    const reviewIds: string[] = []
    const reviewIdToMeetingMap = new Map<string, string>()

    meetings.forEach(meeting => {
      // For professionals: fetch client reviews about them
      // For clients: fetch their own reviews about professionals
      const reviewId = currentUserId === meeting.professionalId
        ? (meeting as any).clientReviewId         // Professional viewing client's rating of them
        : (meeting as any).clientReviewId         // Client viewing their own rating

      if (reviewId) {
        reviewIds.push(reviewId)
        reviewIdToMeetingMap.set(reviewId, meeting.id)
      }
    })

    if (reviewIds.length === 0) {
      return meetings // No reviews to fetch
    }

    // Single efficient batch query for all reviews
    // Using 'in' operator for up to 10 IDs, or multiple batches for larger sets
    const reviewsMap = new Map<string, number>() // meetingId -> rating

    const batchSize = 10 // Firestore 'in' query limit
    for (let i = 0; i < reviewIds.length; i += batchSize) {
      const batch = reviewIds.slice(i, i + batchSize)

      const reviewQuery = query(
        collection(db, 'reviews'),
        where('__name__', 'in', batch)
      )

      const reviewSnapshot = await getDocs(reviewQuery)

      reviewSnapshot.forEach(doc => {
        const review = doc.data() as Review
        const meetingId = reviewIdToMeetingMap.get(doc.id)
        if (meetingId) {
          reviewsMap.set(meetingId, review.rating)
        }
      })
    }

    // Efficiently map ratings back to meetings (O(1) lookup)
    const enrichedMeetings = meetings.map(meeting => ({
      ...meeting,
      clientRating: reviewsMap.get(meeting.id) // Both clients and professionals can see client ratings
    }))

    return enrichedMeetings
  } catch (error) {
    console.error('❌ Error fetching review ratings:', error)
    // Gracefully degrade - return meetings without ratings
    return meetings
  }
}

/**
 * Gets meetings for a specific user using optimized user-centric subcollections
 * Efficiently fetches review ratings in a single batch query
 */
export const getUserMeetings = async (
  userId: string,
  filters?: MeetingFilters,
  userTimezone?: string,
  includeRatings: boolean = true
): Promise<MeetingListItem[]> => {
  try {
    // Getting meetings with optimized queries

    // Query user's subcollections (much faster than OR queries)
    const upcomingMeetings = await getUserMeetingsFromCollection(userId, 'upcoming', filters, userTimezone)
    const recentMeetings = await getUserMeetingsFromCollection(userId, 'recent', filters, userTimezone)
    const archivedMeetings = filters?.dateFrom && isOldDateFilter(filters.dateFrom)
      ? await getUserMeetingsFromCollection(userId, 'archived', filters, userTimezone)
      : []

    // Combine and sort all meetings
    const allMeetings = [...upcomingMeetings, ...recentMeetings, ...archivedMeetings]

    // Sort by startTime (newest first)
    allMeetings.sort((a, b) => new Date(b.datetime).getTime() - new Date(a.datetime).getTime())

    // Retrieved meetings successfully

    // Enrich meetings with profile images
    let enrichedMeetings = allMeetings
    if (allMeetings.length > 0) {
      enrichedMeetings = await enrichMeetingsWithProfileImages(allMeetings, userId)
    }

    // Efficiently fetch review ratings if requested
    if (includeRatings && enrichedMeetings.length > 0) {
      return await enrichMeetingsWithRatings(enrichedMeetings, userId)
    }

    return enrichedMeetings
  } catch (error) {
    console.error('❌ Error getting user meetings:', error)

    // Fallback to legacy method if optimized fails
    return getUserMeetingsLegacy(userId, filters, userTimezone)
  }
}

/**
 * Helper function to query a specific user meeting subcollection
 */
const getUserMeetingsFromCollection = async (
  userId: string,
  collectionType: 'upcoming' | 'recent' | 'archived',
  filters?: MeetingFilters,
  userTimezone?: string
): Promise<MeetingListItem[]> => {
  try {
    const userMeetingsRef = collection(db, 'userMeetings', userId, collectionType)

    let q = query(userMeetingsRef, orderBy('startTime', 'desc'))

    // Add filters
    if (filters?.status && filters.status.length > 0) {
      q = query(q, where('status', 'in', filters.status))
    }

    if (filters?.type && filters.type.length > 0) {
      q = query(q, where('type', 'in', filters.type))
    }

    if (filters?.dateFrom) {
      q = query(q, where('startTime', '>=', Timestamp.fromDate(filters.dateFrom)))
    }

    if (filters?.dateTo) {
      const endOfDay = new Date(filters.dateTo)
      endOfDay.setHours(23, 59, 59, 999)
      q = query(q, where('startTime', '<=', Timestamp.fromDate(endOfDay)))
    }

    const snapshot = await getDocs(q)
    const meetings: MeetingListItem[] = []

    snapshot.forEach((doc) => {
      const meeting = { id: doc.id, ...doc.data() } as Meeting & { otherParticipant?: any }
      meetings.push(formatOptimizedMeetingForList(meeting, userId, userTimezone))
    })

    return meetings
  } catch (error) {
    console.error(`Error getting ${collectionType} meetings for user ${userId}:`, error)
    return []
  }
}

/**
 * Legacy fallback method using original OR queries
 */
const getUserMeetingsLegacy = async (
  userId: string,
  filters?: MeetingFilters,
  userTimezone?: string
): Promise<MeetingListItem[]> => {
  try {
    const meetingsRef = collection(db, 'meetings')

    // Build query conditions using the old approach that works
    let q = query(meetingsRef)

    // Add user condition
    q = query(q, or(
      where('clientId', '==', userId),
      where('professionalId', '==', userId)
    ))

    // Add status filter
    if (filters?.status && filters.status.length > 0) {
      q = query(q, where('status', 'in', filters.status))
    }

    // Add type filter
    if (filters?.type && filters.type.length > 0) {
      q = query(q, where('type', 'in', filters.type))
    }

    // Add date range filters
    if (filters?.dateFrom) {
      q = query(q, where('startTime', '>=', Timestamp.fromDate(filters.dateFrom)))
    }

    if (filters?.dateTo) {
      const endOfDay = new Date(filters.dateTo)
      endOfDay.setHours(23, 59, 59, 999)
      q = query(q, where('startTime', '<=', Timestamp.fromDate(endOfDay)))
    }

    // Add ordering
    q = query(q, orderBy('startTime', 'desc'))

    const querySnapshot = await getDocs(q)
    const meetings: MeetingListItem[] = []

    querySnapshot.forEach((doc) => {
      const meeting = { id: doc.id, ...doc.data() } as Meeting
      meetings.push(formatMeetingForList(meeting, userId, userTimezone))
    })

    return meetings
  } catch (error) {
    console.error('Error in legacy meeting query:', error)
    return []
  }
}

/**
 * Helper to determine if date filter requires archived data
 */
const isOldDateFilter = (dateFrom: Date): boolean => {
  const threeMonthsAgo = new Date()
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3)
  return dateFrom < threeMonthsAgo
}

/**
 * Optimized formatter for meetings with otherParticipant data
 */
function formatOptimizedMeetingForList(
  meeting: Meeting & { otherParticipant?: any },
  currentUserId: string,
  userTimezone?: string
): MeetingListItem {
  const startDate = meeting.startTime.toDate()
  const now = new Date()

  // Use pre-computed otherParticipant data if available
  const otherParticipant = meeting.otherParticipant || {
    name: currentUserId === meeting.clientId ? meeting.professionalName : meeting.clientName,
    imageUrl: currentUserId === meeting.clientId ? meeting.professionalPhotoUrl : meeting.clientPhotoUrl
  }

  // Format date and time with timezone conversion
  let date: string, time: string, datetime: string

  if (userTimezone) {
    const convertedTime = convertMeetingTimeFromGMT(
      meeting.startTime.toDate().toISOString(),
      userTimezone
    )
    date = convertedTime.date
    time = convertedTime.time
    datetime = convertedTime.datetime
  } else {
    date = formatMeetingDate(startDate)
    time = formatMeetingTime(startDate)
    datetime = meeting.startTime.toDate().toISOString()
  }

  // Determine if user can join (15 minutes before to end of meeting)
  const canJoinTime = new Date(startDate.getTime() - 15 * 60 * 1000)
  const meetingEndTime = new Date(startDate.getTime() + meeting.duration * 60 * 1000)
  const canJoin = meeting.status === 'scheduled' && now >= canJoinTime && now <= meetingEndTime

  // Determine if user can review (meeting completed and user hasn't reviewed yet)
  const isClient = meeting.clientId === currentUserId
  const canReview = meeting.status === 'completed' &&
    startDate < now &&
    (isClient ? !meeting.clientReviewSubmitted : !meeting.professionalReviewSubmitted)

  return {
    id: meeting.id,
    date,
    time,
    datetime,
    name: otherParticipant.name,
    imageUrl: otherParticipant.imageUrl,
    location: isClient ? (meeting.professionalTitle || meeting.professionalName) : meeting.clientName,
    clientId: meeting.clientId,
    professionalId: meeting.professionalId,
    duration: meeting.duration,
    type: meeting.type,
    status: meeting.status,
    amount: meeting.amount,
    currency: meeting.currency,
    canJoin,
    canReview,
    clientReviewSubmitted: meeting.clientReviewSubmitted,
    professionalReviewSubmitted: meeting.professionalReviewSubmitted,
    description: meeting.description,
    // Video call details
    meetingRoomId: meeting.meetingRoomId,
    meetingRoomUrl: meeting.meetingRoomUrl,
    joinCode: meeting.joinCode,
    // Include review IDs for efficient rating fetching (DATABASE_SCHEMA.md)
    clientReviewId: meeting.clientReviewId,
    professionalReviewId: meeting.professionalReviewId
  }
}

/**
 * Gets upcoming meetings for a user (includes both scheduled and in-progress)
 */
export const getUpcomingMeetings = async (userId: string, userTimezone?: string): Promise<MeetingListItem[]> => {
  const now = new Date()

  // Get scheduled meetings (starting from now)
  const scheduledMeetings = await getUserMeetings(userId, {
    status: ['scheduled'],
    dateFrom: now
  }, userTimezone)

  // Get in-progress meetings (may have started before now)
  const inProgressMeetings = await getUserMeetings(userId, {
    status: ['in_progress']
  }, userTimezone)

  // Combine and remove duplicates
  const allMeetings = [...scheduledMeetings, ...inProgressMeetings]
  const uniqueMeetings = allMeetings.filter((meeting, index, self) =>
    index === self.findIndex(m => m.id === meeting.id)
  )

  // Sort by date/time
  return uniqueMeetings.sort((a, b) =>
    new Date(a.datetime).getTime() - new Date(b.datetime).getTime()
  )
}

/**
 * Gets past meetings for a user
 */
export const getPastMeetings = async (userId: string, userTimezone?: string): Promise<MeetingListItem[]> => {
  const now = new Date()
  return getUserMeetings(userId, {
    status: ['completed', 'cancelled', 'no_show'],
    dateTo: now
  }, userTimezone)
}

/**
 * Gets meetings that can be reviewed by the user
 */
export const getReviewableMeetings = async (userId: string): Promise<MeetingListItem[]> => {
  try {
    const meetingsRef = collection(db, 'meetings')

    const q = query(
      meetingsRef,
      and(
        or(
          where('clientId', '==', userId),
          where('professionalId', '==', userId)
        ),
        where('status', '==', 'completed'),
        where('endTime', '<=', Timestamp.now())
      ),
      orderBy('endTime', 'desc')
    )

    const querySnapshot = await getDocs(q)
    const meetings: MeetingListItem[] = []

    querySnapshot.forEach((doc) => {
      const meeting = { id: doc.id, ...doc.data() } as Meeting
      const listItem = formatMeetingForList(meeting, userId)

      // Only include if user hasn't submitted review yet
      const isClient = meeting.clientId === userId
      const hasReviewed = isClient
        ? meeting.clientReviewSubmitted
        : meeting.professionalReviewSubmitted

      if (!hasReviewed) {
        meetings.push(listItem)
      }
    })

    return meetings
  } catch (error) {
    console.error('Error getting reviewable meetings:', error)
    return []
  }
}

/**
 * Gets a specific meeting by ID
 */
export const getMeeting = async (meetingId: string): Promise<Meeting | null> => {
  try {
    const meetingDoc = await getDoc(doc(db, 'meetings', meetingId))

    if (meetingDoc.exists()) {
      return { id: meetingDoc.id, ...meetingDoc.data() } as Meeting
    }

    return null
  } catch (error) {
    console.error('Error getting meeting:', error)
    return null
  }
}

/**
 * Creates a new meeting
 */
export const createMeeting = async (meetingData: CreateMeetingRequest): Promise<string | null> => {
  try {
    const now = Timestamp.now()
    const endTime = new Timestamp(
      meetingData.startTime.seconds + (meetingData.duration * 60),
      meetingData.startTime.nanoseconds
    )

    const meeting: Omit<Meeting, 'id'> = {
      ...meetingData,
      endTime,
      status: 'scheduled',
      paymentStatus: 'pending',
      bookedAt: now,
      createdAt: now,
      updatedAt: now,
      clientReviewSubmitted: false,
      professionalReviewSubmitted: false
    }

    const docRef = await addDoc(collection(db, 'meetings'), meeting)
    return docRef.id
  } catch (error) {
    console.error('Error creating meeting:', error)
    return null
  }
}

/**
 * Updates a meeting
 */
export const updateMeeting = async (
  meetingId: string,
  updates: Partial<Meeting>
): Promise<boolean> => {
  try {
    const updateData = {
      ...updates,
      updatedAt: Timestamp.now()
    }

    // Update main meeting document
    await updateDoc(doc(db, 'meetings', meetingId), updateData)

    // Get the meeting to find client and professional IDs
    const meetingDoc = await getMeeting(meetingId)
    if (meetingDoc) {
      // Update user subcollections as well
      const clientId = meetingDoc.clientId
      const professionalId = meetingDoc.professionalId

      // Update client's meeting copies
      if (clientId) {
        try {
          const clientUpcoming = doc(db, 'userMeetings', clientId, 'upcoming', meetingId)
          await updateDoc(clientUpcoming, updateData).catch(() => {
            // Meeting might not be in upcoming, try recent
            const clientRecent = doc(db, 'userMeetings', clientId, 'recent', meetingId)
            return updateDoc(clientRecent, updateData).catch(() => {
              // Meeting might be in archived, ignore if not found
              console.log(`Meeting ${meetingId} not found in client ${clientId} subcollections`)
            })
          })
        } catch (error) {
          console.warn(`Could not update client subcollection for meeting ${meetingId}:`, error)
        }
      }

      // Update professional's meeting copies
      if (professionalId) {
        try {
          const profUpcoming = doc(db, 'userMeetings', professionalId, 'upcoming', meetingId)
          await updateDoc(profUpcoming, updateData).catch(() => {
            // Meeting might not be in upcoming, try recent
            const profRecent = doc(db, 'userMeetings', professionalId, 'recent', meetingId)
            return updateDoc(profRecent, updateData).catch(() => {
              // Meeting might be in archived, ignore if not found
              console.log(`Meeting ${meetingId} not found in professional ${professionalId} subcollections`)
            })
          })
        } catch (error) {
          console.warn(`Could not update professional subcollection for meeting ${meetingId}:`, error)
        }
      }
    }

    return true
  } catch (error) {
    console.error('Error updating meeting:', error)
    return false
  }
}

/**
 * Marks a meeting as completed
 */
export const completeMeeting = async (meetingId: string): Promise<boolean> => {
  return updateMeeting(meetingId, {
    status: 'completed'
  })
}

/**
 * Marks that a user has submitted a review for a meeting
 */
export const markReviewSubmitted = async (
  meetingId: string,
  userId: string
): Promise<boolean> => {
  try {
    const meeting = await getMeeting(meetingId)
    if (!meeting) return false

    const isClient = meeting.clientId === userId
    const updates = isClient
      ? { clientReviewSubmitted: true }
      : { professionalReviewSubmitted: true }

    return await updateMeeting(meetingId, updates)
  } catch (error) {
    console.error('Error marking review submitted:', error)
    return false
  }
}

/**
 * Cancels a meeting
 */
export const cancelMeeting = async (
  meetingId: string,
  cancelledBy: string,
  reason?: string
): Promise<boolean> => {
  return updateMeeting(meetingId, {
    status: 'cancelled',
    cancelledAt: Timestamp.now(),
    cancelledBy,
    cancellationReason: reason
  })
}

/**
 * Formats a meeting for display in lists
 */
function formatMeetingForList(meeting: Meeting, currentUserId: string, userTimezone?: string): MeetingListItem {
  const startDate = meeting.startTime.toDate()
  const now = new Date()

  // Determine if current user is client or professional
  const isClient = meeting.clientId === currentUserId
  const otherParticipant = isClient ? {
    name: meeting.professionalName,
    imageUrl: meeting.professionalPhotoUrl
  } : {
    name: meeting.clientName,
    imageUrl: meeting.clientPhotoUrl
  }

  // Format date and time with timezone conversion
  let date: string, time: string, datetime: string

  if (userTimezone) {
    // Convert from GMT to user's timezone
    const convertedTime = convertMeetingTimeFromGMT(
      meeting.startTime.toDate().toISOString(),
      userTimezone
    )
    date = convertedTime.date
    time = convertedTime.time
    datetime = convertedTime.datetime
  } else {
    // Fallback to original formatting (no timezone conversion)
    date = formatMeetingDate(startDate)
    time = formatMeetingTime(startDate)
    datetime = meeting.startTime.toDate().toISOString()
  }

  // Determine if user can join (15 minutes before to end of meeting)
  const canJoinTime = new Date(startDate.getTime() - 15 * 60 * 1000) // 15 min before
  const meetingEndTime = new Date(startDate.getTime() + meeting.duration * 60 * 1000) // End of meeting
  const canJoin = meeting.status === 'scheduled' && now >= canJoinTime && now <= meetingEndTime

  // Determine if user can review (meeting completed and user hasn't reviewed yet)
  const canReview = meeting.status === 'completed' &&
    startDate < now &&
    (isClient ? !meeting.clientReviewSubmitted : !meeting.professionalReviewSubmitted)

  return {
    id: meeting.id,
    date,
    time,
    datetime,
    name: otherParticipant.name,
    imageUrl: otherParticipant.imageUrl,
    location: isClient ? (meeting.professionalTitle || meeting.professionalName) : meeting.clientName, // Show title/role here
    clientId: meeting.clientId,
    professionalId: meeting.professionalId,
    duration: meeting.duration,
    type: meeting.type,
    status: meeting.status,
    amount: meeting.amount,
    currency: meeting.currency,
    canJoin,
    canReview,
    clientReviewSubmitted: meeting.clientReviewSubmitted,
    professionalReviewSubmitted: meeting.professionalReviewSubmitted,
    description: meeting.description,
    // Video call details
    meetingRoomId: meeting.meetingRoomId,
    meetingRoomUrl: meeting.meetingRoomUrl,
    joinCode: meeting.joinCode,
    // Include review IDs for efficient rating fetching (DATABASE_SCHEMA.md)
    clientReviewId: meeting.clientReviewId,
    professionalReviewId: meeting.professionalReviewId
  }
}

/**
 * Formats meeting date for display
 */
function formatMeetingDate(date: Date): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  }

  const formatted = date.toLocaleDateString('en-US', options)

  // Convert to ordinal format (January 25th, 2025)
  const day = date.getDate()
  const suffix = getDaySuffix(day)

  return formatted.replace(String(day), `${day}${suffix}`)
}

/**
 * Formats meeting time for display
 */
function formatMeetingTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

/**
 * Gets the ordinal suffix for a day number
 */
function getDaySuffix(day: number): string {
  if (day >= 11 && day <= 13) {
    return 'th'
  }

  switch (day % 10) {
    case 1: return 'st'
    case 2: return 'nd'
    case 3: return 'rd'
    default: return 'th'
  }
}

/**
 * Checks if user can join a meeting now
 */
export const canJoinMeeting = (datetime: string): boolean => {
  const meetingTime = new Date(datetime)
  const now = new Date()

  // Can join 15 minutes before to 15 minutes after start time
  const canJoinTime = new Date(meetingTime.getTime() - 15 * 60 * 1000)
  const joinDeadline = new Date(meetingTime.getTime() + 15 * 60 * 1000)

  return now >= canJoinTime && now <= joinDeadline
}

/**
 * Gets time until meeting starts
 */
export const getTimeUntilMeeting = (datetime: string): string => {
  const meetingTime = new Date(datetime)
  const now = new Date()
  const diffMs = meetingTime.getTime() - now.getTime()

  if (diffMs <= 0) {
    return 'Meeting time has passed'
  }

  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  const diffDays = Math.floor(diffHours / 24)

  if (diffDays > 0) {
    return `${diffDays} day${diffDays > 1 ? 's' : ''}`
  } else if (diffHours > 0) {
    return `${diffHours} hour${diffHours > 1 ? 's' : ''} ${diffMinutes} min`
  } else {
    return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`
  }
}

/**
 * Gets booked meetings for a professional within a specific date range
 * Used for calendar conflict detection to hide availability blocks when already booked
 */
export const getProfessionalBookedMeetings = async (
  professionalId: string,
  startDate: Date,
  endDate: Date
): Promise<{ startTime: Date; endTime: Date; meetingId: string }[]> => {
  try {
    const meetingsRef = collection(db, 'meetings')

    const q = query(
      meetingsRef,
      and(
        where('professionalId', '==', professionalId),
        where('status', 'in', ['scheduled', 'in_progress']),
        where('startTime', '>=', Timestamp.fromDate(startDate)),
        where('startTime', '<=', Timestamp.fromDate(endDate))
      ),
      orderBy('startTime', 'asc')
    )

    const querySnapshot = await getDocs(q)
    const bookedSlots: { startTime: Date; endTime: Date; meetingId: string }[] = []

    querySnapshot.forEach((doc) => {
      const meeting = { id: doc.id, ...doc.data() } as Meeting
      bookedSlots.push({
        startTime: meeting.startTime.toDate(),
        endTime: meeting.endTime.toDate(),
        meetingId: meeting.id
      })
    })

    return bookedSlots
  } catch (error) {
    console.error('Error getting professional booked meetings:', error)
    return []
  }
}